/**
 * Event Incidents Page
 *
 * Coach/Admin page that lists incidents reported against a single practice
 * event and launches the IncidentCaptureDialog for reporting new ones.
 *
 * Behaviour overview:
 * - Reads `:eventId` from the URL (`/events/:eventId/incidents`).
 * - On mount, fires both `getIncidentsByEvent(eventId)` and
 *   `getRiders(currentSeason)` in parallel via `Promise.all`.
 * - Filters the roster to active current-season riders
 *   (`rider.disabled !== '1' && rider.disabled !== 1`) — Property 18.
 * - Header: event_name + event_starttime resolved in this priority:
 *     1) First returned incident's enrichment (`event_name` / `event_date`)
 *     2) Dashboard route state (`location.state.event_name` / `event_starttime`)
 *     3) `restClient.getEvent(eventId)` as a last resort.
 * - List: sorted by `timestamp` desc client-side INSIDE try/catch. If any
 *   `new Date(i.timestamp)` yields NaN, preserve the API order returned by
 *   the Lambda (which uses ScanIndexForward=False on the GSI) — Property 26.
 * - Empty state literal: "No incidents reported for this practice yet".
 * - "Report Incident" button opens the shared IncidentCaptureDialog. On
 *   success the list is refetched in place (no full-page reload).
 * - Back → referring page (history-aware, falls back to /dashboard) via
 *   useNavigate. Uses `navigate(-1)` when `window.history.length > 1`
 *   so the button returns to IncidentsReport, EventDetail, Dashboard,
 *   or whichever page linked here. For direct URLs with no history
 *   entry, we drop the user on /dashboard so they aren't stranded.
 * - When the caller is an admin, render a link to /admin/reports/incidents.
 *   Hidden for coaches and regular users — the Incidents Report is an
 *   admin-only surface (the route guard also blocks non-admins).
 *
 * Mobile-first: renders without horizontal scrolling from 320px–480px,
 * every interactive control has `min-h-[44px]`.
 *
 * Implements Requirements:
 *   2.1, 18.2, 18.11, 18.17 (mount fetches + filtered roster)
 *   18.4, 18.5, 18.6, 18.7 (header, list, empty state)
 *   16.5                    (time-of-day timestamp on row)
 *   18.8, 18.9, 18.16       (capture dialog wiring + refresh on success)
 *   18.10, 18.12, 18.13     (loading, error/retry, back navigation)
 *   18.17, 18.18, 18.19     (coach/admin cross-link to report page)
 *   12.5, 18.1, 18.14       (route is registered with ProtectedRoute requireCoach)
 *
 * See: design.md §Components and Interfaces → Component hierarchy (detail) →
 *      EventIncidents; §Frontend Design → State management and data fetching;
 *      §Open Questions / Deferred Decisions #4.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { restClient, Rider, Event as ApiEvent } from '../api/rest-client';
import type { Incident } from '../types/incidents';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { useUIStore } from '../store/uiStore';
import { formatRiderName } from '../utils/riderDisplay';
import { IncidentCaptureDialog } from '../components/features/incidents/IncidentCaptureDialog';

// Cutoff for inline-expanding the description preview (Req 18.5).
const DESCRIPTION_PREVIEW_LIMIT = 200;

/**
 * Type guard for the Dashboard-provided route state. `useLocation().state`
 * is typed `unknown` by default in react-router 7, so we narrow it safely
 * before reading the two fields we care about.
 */
function readRouteState(state: unknown): {
  event_name?: string;
  event_starttime?: string;
} {
  if (!state || typeof state !== 'object') {
    return {};
  }
  const s = state as Record<string, unknown>;
  const out: { event_name?: string; event_starttime?: string } = {};
  if (typeof s.event_name === 'string') {
    out.event_name = s.event_name;
  }
  if (typeof s.event_starttime === 'string') {
    out.event_starttime = s.event_starttime;
  }
  return out;
}

/**
 * Extract a user-facing error message from a thrown error. Prefers the
 * server-provided `error` field on the response body; falls back to a
 * generic network-level message.
 */
function extractLoadErrorMessage(err: unknown): string {
  const maybe = err as {
    response?: { data?: { error?: unknown; message?: unknown } };
    message?: unknown;
  };
  const serverError = maybe?.response?.data?.error;
  if (typeof serverError === 'string' && serverError.length > 0) {
    return serverError;
  }
  const serverMessage = maybe?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.length > 0) {
    return serverMessage;
  }
  if (!maybe?.response) {
    // Network-level failure before the response arrives (Req 18.13).
    return 'Could not load incidents';
  }
  if (typeof maybe?.message === 'string' && maybe.message.length > 0) {
    return maybe.message;
  }
  return 'Could not load incidents';
}

/**
 * Render an incident's timestamp as a time of day (e.g., "6:42 PM"). Falls
 * back to the raw string if the value can't be parsed so the row still
 * renders (Req 16.5, Property 26 — never throw on bad timestamps).
 */
function formatTimestampTimeOfDay(iso: string | undefined | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(iso ?? '');
  }
}

/**
 * Format an event start time for the page header.
 */
function formatEventStart(iso: string | undefined | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(iso ?? '');
  }
}

/**
 * Sort incidents by `timestamp` descending. If ANY timestamp fails to
 * parse, fall back to the original API order (the Lambda already returns
 * with `ScanIndexForward=False` on the GSI). Never throws — Property 26.
 */
function sortIncidentsDesc(incidents: Incident[]): Incident[] {
  try {
    // Detect malformed timestamps up front so a partial sort never
    // produces a non-deterministic order.
    for (const inc of incidents) {
      const t = new Date(inc.timestamp).getTime();
      if (Number.isNaN(t)) {
        return incidents;
      }
    }
    return [...incidents].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  } catch {
    return incidents;
  }
}

/** Truncate a description for inline preview with a "Show more" toggle. */
function IncidentDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const safe = typeof text === 'string' ? text : '';
  const needsTruncation = safe.length > DESCRIPTION_PREVIEW_LIMIT;
  if (!needsTruncation) {
    return <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{safe}</p>;
  }
  return (
    <div className="text-sm text-gray-800">
      <p className="whitespace-pre-wrap break-words">
        {expanded ? safe : `${safe.slice(0, DESCRIPTION_PREVIEW_LIMIT)}…`}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-primary-600 hover:text-primary-700 underline text-sm min-h-[44px]"
        aria-expanded={expanded}
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}

export default function EventIncidents() {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const showToast = useUIStore((state) => state.showToast);

  // Route state carried from the Dashboard (Req 18.4 fallback source #2).
  const routeState = useMemo(() => readRouteState(location.state), [location.state]);

  // Data state.
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Header state when no incidents exist and no route state was provided
  // (Req 18.4 fallback source #3 — `restClient.getEvent`).
  const [eventFallback, setEventFallback] = useState<ApiEvent | null>(null);

  // Capture dialog state (Req 18.8).
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // Admin-only edit/delete state (post-launch enhancement). Keeping both
  // the target incident and a ref to the triggering row lets us restore
  // focus to the row after the modal closes for keyboard users.
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [deletingIncident, setDeletingIncident] = useState<Incident | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const actionTriggerRef = useRef<HTMLElement | null>(null);

  /** Parallel mount fetches (Req 18.2, 18.17). */
  const loadData = useCallback(async () => {
    if (!eventId) {
      setLoading(false);
      setLoadError('Missing event id in URL');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const currentSeason = new Date().getFullYear().toString();
      const [incidentsRes, ridersRes] = await Promise.all([
        restClient.getIncidentsByEvent(eventId),
        restClient.getRiders(currentSeason),
      ]);
      setIncidents(Array.isArray(incidentsRes) ? incidentsRes : []);
      setRiders(Array.isArray(ridersRes) ? ridersRes : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventIncidents] Failed to load page data', err);
      setLoadError(extractLoadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Active-roster filter for the capture dialog (Req 18.11, Property 18).
  const activeRoster = useMemo(
    () => riders.filter((r) => r.disabled !== '1' && r.disabled !== 1),
    [riders],
  );

  // Sorted list for the main view (Req 18.6, Property 26).
  const sortedIncidents = useMemo(() => sortIncidentsDesc(incidents), [incidents]);

  // Header resolution:
  //   1) First incident enrichment (event_name / event_date)
  //   2) Dashboard route state (event_name / event_starttime)
  //   3) restClient.getEvent fallback (populated on demand below)
  const firstIncident = sortedIncidents[0];
  const resolvedEventName =
    firstIncident?.event_name ||
    routeState.event_name ||
    eventFallback?.event_name ||
    '';
  const resolvedEventStartTime =
    firstIncident?.event_date ||
    routeState.event_starttime ||
    eventFallback?.event_starttime ||
    '';

  // If we have no incidents AND no route state, fall back to getEvent.
  useEffect(() => {
    if (loading) return;
    if (loadError) return;
    if (!eventId) return;
    const havePrimary = Boolean(firstIncident?.event_name || firstIncident?.event_date);
    const haveRouteState = Boolean(routeState.event_name || routeState.event_starttime);
    if (havePrimary || haveRouteState) return;
    if (eventFallback) return;
    let cancelled = false;
    restClient
      .getEvent(eventId)
      .then((ev) => {
        if (!cancelled) setEventFallback(ev);
      })
      .catch((err) => {
        // Non-fatal — the header will simply be blank (Req 18.4 best effort).
        // eslint-disable-next-line no-console
        console.warn('[EventIncidents] Event fallback fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [loading, loadError, eventId, firstIncident, routeState, eventFallback]);

  const canSeeReportsLink = isAdmin;

  const handleDialogSuccess = useCallback(async () => {
    // Refresh the incidents list in place; no full page reload (Req 18.9).
    try {
      const fresh = await restClient.getIncidentsByEvent(eventId);
      setIncidents(Array.isArray(fresh) ? fresh : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventIncidents] Failed to refresh incidents after submit', err);
    }
  }, [eventId]);

  /**
   * Admin-only: open the capture dialog in edit mode prefilled with the
   * clicked row's current values. The dialog handles validation +
   * submission; on success we simply refresh the list via the same
   * `handleDialogSuccess` path already used for create.
   */
  const handleOpenEdit = useCallback(
    (incident: Incident, trigger: HTMLElement | null) => {
      actionTriggerRef.current = trigger;
      setEditingIncident(incident);
    },
    [],
  );

  const handleCloseEdit = useCallback(() => {
    setEditingIncident(null);
    // Restore focus to the row's action trigger so keyboard users don't
    // lose their place (WCAG 2.4.3).
    setTimeout(() => {
      try {
        actionTriggerRef.current?.focus();
      } catch {
        /* best-effort focus return */
      }
    }, 0);
  }, []);

  /**
   * Admin-only: open the confirm modal for deletion. The delete itself
   * runs in `handleConfirmDelete` after the user confirms.
   */
  const handleOpenDelete = useCallback(
    (incident: Incident, trigger: HTMLElement | null) => {
      actionTriggerRef.current = trigger;
      setDeletingIncident(incident);
      setDeleteError(null);
    },
    [],
  );

  const handleCloseDelete = useCallback(() => {
    setDeletingIncident(null);
    setDeleteError(null);
    setDeleting(false);
    setTimeout(() => {
      try {
        actionTriggerRef.current?.focus();
      } catch {
        /* best-effort focus return */
      }
    }, 0);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingIncident) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await restClient.deleteIncident(deletingIncident.incident_id);
      const deletedRiderName =
        formatRiderName({
          name: deletingIncident.rider_name ?? '',
          type: undefined,
        }) || 'the incident';
      showToast('success', `Deleted incident for ${deletedRiderName}`);
      // Refresh the list in place. We do this BEFORE closing the modal
      // so the user sees the row disappear without a flicker.
      try {
        const fresh = await restClient.getIncidentsByEvent(eventId);
        setIncidents(Array.isArray(fresh) ? fresh : []);
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[EventIncidents] Failed to refresh incidents after delete',
          refreshErr,
        );
      }
      handleCloseDelete();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventIncidents] Failed to delete incident', err);
      setDeleteError(extractLoadErrorMessage(err));
      setDeleting(false);
    }
  }, [deletingIncident, eventId, showToast, handleCloseDelete]);

  /**
   * Back-button handler — returns the user to wherever they came from.
   *
   * Using `navigate(-1)` rather than a hard-coded destination means the
   * button round-trips cleanly from IncidentsReport, EventDetail, or the
   * Dashboard (the three legitimate entry points today). For direct URLs
   * where `window.history.length <= 1` there's nothing to go "back" to,
   * so we drop the user on /dashboard instead of leaving them stranded.
   */
  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Top action row — Back + cross-link to reports */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <Button
          type="button"
          variant="secondary"
          onClick={handleBack}
          className="min-h-[44px] px-4"
          aria-label="Back"
        >
          ← Back
        </Button>
        {canSeeReportsLink && (
          <Link
            to="/admin/reports/incidents"
            className="inline-flex items-center justify-center min-h-[44px] px-4 text-primary-600 hover:text-primary-700 underline text-sm"
          >
            View all incidents
          </Link>
        )}
      </div>

      {/* Page title + event header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-primary-900">Practice Incidents</h1>
        {(resolvedEventName || resolvedEventStartTime) && (
          <div className="mt-1">
            {resolvedEventName && (
              <p className="text-base font-semibold text-gray-800 break-words">
                {resolvedEventName}
              </p>
            )}
            {resolvedEventStartTime && (
              <p className="text-sm text-gray-600">
                {formatEventStart(resolvedEventStartTime)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Primary CTA — always visible so the coach can report even when the
          list is empty or the fetch failed. */}
      <div className="mb-4">
        <Button
          type="button"
          variant="primary"
          onClick={() => setDialogOpen(true)}
          className="min-h-[44px] px-6"
        >
          Report Incident
        </Button>
      </div>

      {/* Loading / Error / List */}
      {loading && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading incidents…</span>
        </div>
      )}

      {!loading && loadError && (
        <Card className="border border-red-300 bg-red-50">
          <div role="alert" aria-live="assertive" className="text-sm text-red-800">
            <p className="font-semibold">{loadError}</p>
          </div>
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={loadData}
              className="min-h-[44px] px-4"
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {!loading && !loadError && sortedIncidents.length === 0 && (
        <Card>
          <p className="text-gray-700">No incidents reported for this practice yet</p>
        </Card>
      )}

      {!loading && !loadError && sortedIncidents.length > 0 && (
        <ul className="space-y-3" aria-label="Incidents list">
          {sortedIncidents.map((inc) => {
            const displayedRiderName =
              formatRiderName({ name: inc.rider_name ?? '', type: undefined }) ||
              'Unknown Rider';
            const reportedBy =
              typeof inc.recorded_by_display_name === 'string' &&
              inc.recorded_by_display_name.length > 0
                ? inc.recorded_by_display_name
                : (inc.recorded_by || '');
            return (
              <li key={inc.incident_id}>
                <Card>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-base font-semibold text-gray-900 break-words">
                          {displayedRiderName}
                        </p>
                        <p className="text-sm text-gray-600">{inc.incident_type}</p>
                      </div>
                      <p className="text-sm text-gray-500 whitespace-nowrap">
                        {formatTimestampTimeOfDay(inc.timestamp)}
                      </p>
                    </div>

                    <IncidentDescription text={inc.description} />

                    <p className="text-xs text-gray-500">
                      <span className="font-medium">Reported By:</span>{' '}
                      {reportedBy || '—'}
                    </p>

                    {isAdmin && (
                      // Admin-only edit/delete action row. Hidden for coaches
                      // and regular users — the backend enforces the same
                      // restriction (practice-incidents-update.py / -delete.py
                      // reject non-admins with 403), so this is defense-in-
                      // depth at the UI layer.
                      <div className="pt-1 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="orange"
                          onClick={(e) =>
                            handleOpenEdit(inc, e.currentTarget as HTMLElement)
                          }
                          className="min-h-[44px] px-4 text-sm"
                          aria-label={`Edit incident for ${displayedRiderName}`}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={(e) =>
                            handleOpenDelete(inc, e.currentTarget as HTMLElement)
                          }
                          className="min-h-[44px] px-4 text-sm"
                          aria-label={`Delete incident for ${displayedRiderName}`}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/* Capture dialog (Req 18.8, 18.9, 18.16). */}
      {dialogOpen && (
        <IncidentCaptureDialog
          eventId={eventId}
          eventName={resolvedEventName}
          eventStartTime={resolvedEventStartTime}
          riders={activeRoster}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={handleDialogSuccess}
        />
      )}

      {/* Admin-only edit dialog — reuses the same component in mode='edit'. */}
      {editingIncident && (
        <IncidentCaptureDialog
          eventId={eventId}
          eventName={resolvedEventName}
          eventStartTime={resolvedEventStartTime}
          riders={activeRoster}
          isOpen={Boolean(editingIncident)}
          onClose={handleCloseEdit}
          onSuccess={handleDialogSuccess}
          mode="edit"
          initialValues={{
            incident_id: editingIncident.incident_id,
            rider_id: editingIncident.rider_id,
            incident_type: editingIncident.incident_type,
            description: editingIncident.description,
          }}
        />
      )}

      {/* Admin-only delete confirm modal. */}
      {deletingIncident && (
        <Modal
          isOpen={Boolean(deletingIncident)}
          onClose={deleting ? () => {} : handleCloseDelete}
          title="Delete Incident"
          className="sm:max-w-md"
          showCloseButton={!deleting}
        >
          <p className="text-sm text-gray-800">
            Delete this incident? This cannot be undone.
          </p>
          {deleteError && (
            <div
              role="alert"
              aria-live="assertive"
              className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
            >
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseDelete}
              disabled={deleting}
              className="min-h-[44px] px-6"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="min-h-[44px] px-6"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
